import moment from "moment";
import React, { Fragment, useEffect, useState } from "react";
import {
  createStartOfDayWithTimeZone,
  DateFormats,
  formatDate,
  getMinuteDifference,
  ILeadAction,
  IUpdateEventModel,
  Lead,
  getInvalidCustomerFieldsTestDrive,
  Customer,
} from "../../../../core";
import {
  IBigCalendarEvent,
  IBigCalendarResource,
} from "../../../../core/components/calendar/big-calendar-component";
import { getActivityDisplayDetails } from "../../../../core/components/calendar/calendar-mapper-utils";
import { EditEventCalendarWraper } from "../../../../core/components/calendar/edit-event-calendar-wraper";
import { ITimePeriodData } from "../../../../core/components/time-period/custom-time-period";
import {
  combineDateAndTime,
  combineDateAndTimeMoment,
} from "../../../../utils/CombineDateAndTime";

import { ActivityModel, AddActivityForm } from "./add-activity";
import { ActivityPopup } from "./edit-activity-popup";
import {
  TimeReservationForm,
  TimeReservationModel,
} from "./time-reservation-form";
import { VehicleTimeReservationForm } from "./vehicle-time-reservation-form";
import { useDataLoader } from "react-remote-data-hooks";
import api from "../../../../api";

interface IEditEventPopupProps {
  currentActivity?: ActivityModel;
  leadId?: string;
  onSubmit: (event: IUpdateEventModel) => void;
  onClose: () => void;
  isEdit: boolean;
  action?: ILeadAction;
  leads?: Lead[];
  customerId?: string;
}

export const EditEventPopup: React.FunctionComponent<IEditEventPopupProps> = (
  props
) => {
  const [selectedDate, setSelectedDate] = useState(
    props.currentActivity?.timePeriod?.startDate ?? new Date()
  );
  const [currentActivity, setCurrentActivity] = useState(props.currentActivity);

  const [isCustomerAvailable, setIsCustomerAvailable] = React.useState<boolean>(
    false
  );

  const [tempEvent, setTempEvent] = useState<IBigCalendarEvent>();

  const { data: excludedDatesData } = useDataLoader<Date[]>(() =>
    api.holiday.getExcludedDatesForEvents()
  );
  const [filter, setFilter] = useState<IFormFilterData>({
    salesPerson: props.currentActivity?.salesPerson?._id,
    vehicle: props.currentActivity?.vehicle?._id,
    showRoom: props.currentActivity?.showRoom?._id,
  });

  const [activityType, setActivityType] = useState<string>();

  const { data: customerDetails } = useDataLoader<Customer>(() =>
    api.customer.getDetails(props.customerId || "")
  );

  const closePopup = () => {
    setFilter({
      salesPerson: props.currentActivity?.salesPerson?._id,
      // showRoom: props.currentActivity?.showRoom?._id,
    });
    props.onClose();
  };
  const onTimePeriodChange = (data?: ITimePeriodData) => {
    if (data) {
      if (tempEvent) {
        const clone = { ...tempEvent };
        const startDate = data.startDate || new Date();
        const endDate = data.endDate ?? data.startDate!;

        clone.start = startDate;
        clone.end = endDate;
        if (data.startTime) {
          clone.start = combineDateAndTime(clone.start, data.startTime);
        }
        if (data.endTime) {
          clone.end = combineDateAndTime(clone.end, data.endTime);
        }

        setTempEvent(clone);
        if (currentActivity) {
          const activityClone = { ...currentActivity, timePeriod: data };
          setCurrentActivity(activityClone);
        }
      }
      setSelectedDate(data.startDate || new Date());
    }
  };

  useEffect(() => {
    if (tempEvent && filter.salesPerson) {
      setTempEvent({ ...tempEvent, resourceId: filter.salesPerson });
    }
  }, [filter.salesPerson]);

  useEffect(() => {
    if (
      tempEvent &&
      filter.vehicle &&
      currentActivity?.type !== "scheduleATestDrive"
    ) {
      const clone = { ...tempEvent };
      clone.resourceId = filter.vehicle;
      setTempEvent(clone);
    }
  }, [filter.vehicle]);

  useEffect(() => {
    mapToBigCalendarTempEvent();
  }, []);

  useEffect(() => {
    mapToBigCalendarTempEvent();
  }, [currentActivity]);

  const mapToBigCalendarTempEvent = () => {
    const activity = currentActivity;

    if (
      activity?.timePeriod?.startDate != null &&
      activity?.timePeriod.startTime !== "" &&
      activity?.timePeriod.endTime !== ""
    ) {
      const startDate = combineDateAndTimeMoment(
        moment(activity?.timePeriod?.startDate).format("YYYY-MM-DD"),
        activity.timePeriod.startTime!
      );
      const endDate = combineDateAndTimeMoment(
        moment(
          activity.timePeriod.endDate ?? activity.timePeriod.startDate
        ).format("YYYY-MM-DD"),
        activity.timePeriod.endTime!
      );

      const event: IBigCalendarEvent = {
        id: activity?.eventId ?? "",
        title: activity?.title ?? "",
        start: startDate,
        end: endDate,
        resourceId: activity.salesPerson?._id ?? filter.salesPerson!,
        appointment: {} as any,
        activityType: "temp",
        displayDetails: {
          icon: "",
          text: "Temp",
          color: "4161C8",
          isDraggable: true,
          isSingleDayEvent: true,
          className: "temp-event",
        },
      };

      if (isVehicleOnlyView()) {
        event.resourceId = activity.vehicle?._id ?? filter.vehicle!;
      }

      setTempEvent(event);
    } else {
      setTempEvent(undefined);
    }
  };
  const onSubmitActivity = (
    data: ActivityModel & { contactUserIds?: string[] }
  ) => {
    const startDate = combineDateAndTime(
      data?.timePeriod?.startDate!,
      data?.timePeriod?.startTime!
    );
    let endDate = combineDateAndTime(
      data?.timePeriod?.endDate ?? data.timePeriod?.startDate!,
      data?.timePeriod?.endTime!
    );
    if (data.activityType !== "timeOff" && data.type !== "scheduleATestDrive") {
      endDate = combineDateAndTime(
        data.timePeriod?.startDate!,
        data?.timePeriod?.endTime!
      );
    }

    const apiModel: IUpdateEventModel = {
      type: data.type,
      activityType: data?.activityType,
      title: data.title,
      startDate,
      endDate,
      assignedPersonId: data.salesPerson?._id,
      leadId: props.leadId,
      vehicleModelId: data.vehicleModel?._id,
      vehicleId: data?.vehicleId ?? data.vehicleVariant?._id,
      note: data.note,
      showroom: data.showRoom?._id,
      busyEvent: data.busyEvent,
      testDriveLocation: data.location,
      modelCode: data.modelCode,
      variantCode: data.variantCode,
      year: data.year,
      overnightTestdriveEnabled: data.overnightTestdriveEnabled,
    };
    if (data.contactUserIds?.length) {
      apiModel.users = data.contactUserIds;
    }
    props.onSubmit(apiModel);
  };

  const onDateChange = (date: Date) => {
    setSelectedDate(date);
    if (currentActivity) {
      const clone = { ...currentActivity };

      if (clone.timePeriod == null) {
        clone.timePeriod = {};
      }
      clone.timePeriod.startDate = date;
      setCurrentActivity(clone);
    }
  };

  const mapToTimeReservation = () => {
    const data = currentActivity;
    if (data) {
      const reservation: TimeReservationModel = {
        timePeriod: data.timePeriod,
        salesPerson: data.salesPerson,
        note: data.note,
        vehicle: data?.vehicle,
        showroom: data?.showRoom,
        vehicleModel: data?.vehicleModel,
        vehicleVariant: data?.vehicleVariant,
      };
      return reservation;
    }
    return {};
  };
  const [
    timeReservationData,
    setTimeReservationData,
  ] = useState<TimeReservationModel>(mapToTimeReservation());

  useEffect(() => {
    setTimeReservationData(mapToTimeReservation());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentActivity]);
  const isValidTime = (start: string, end: string) => {
    const startOfDay = moment(new Date()).startOf("day").toDate();
    const startDateTime = combineDateAndTime(startOfDay, start);
    const endDateTime = combineDateAndTime(startOfDay, end);
    return moment(startDateTime).isBefore(moment(endDateTime));
  };

  const isVehicleOnlyView = () => {
    return currentActivity?.type === "vehicleTimeOff";
  };

  const assignFiltersFromCalendarResource = (
    resource?: IBigCalendarResource
  ) => {
    if (resource) {
      const filterclone = { ...filter };
      if (!isVehicleOnlyView()) {
        filterclone.salesPerson = resource.resourceId;
      } else {
        filterclone.vehicle = resource.resourceId;
      }
      filterclone.showRoom = resource.showroom?._id;
      setFilter(filterclone);
    }
  };

  const assignResourceTypeToActivity = (
    clone: ActivityModel,
    resource?: IBigCalendarResource
  ) => {
    if (resource && resource.resourceType === "staff") {
      clone.salesPerson = {
        _id: resource.resourceId,
        name: resource.resourceTitle,
      };
      clone.showRoom = resource.showroom;
    } else {
      clone.vehicleModel = resource?.vehicleModel;
      clone.vehicleVariant = resource?.vehicleVariant;
      clone.showRoom = resource?.showroom;
      clone.vehicle = {
        _id: resource?.resourceId!,
        displayName: resource?.resourceTitle!,
      };
    }
  };

  const handleOnActivityTypeChange = (currentActivityType: string) => {
    setActivityType(currentActivityType);
  };

  const onSlotSelection = (
    start: Date,
    end: Date,
    resource?: IBigCalendarResource
  ) => {
    if (resource?.resourceType === "vehicle" && !isVehicleOnlyView()) {
      return;
    }
    assignFiltersFromCalendarResource(resource);

    if (currentActivity) {
      const clone = { ...currentActivity };
      assignResourceTypeToActivity(clone, resource);

      clone.timePeriod = {
        startDate: start,
        startTime: formatDate(start, DateFormats.TimeOnlyFormat),
        endTime: formatDate(end, DateFormats.TimeOnlyFormat),
        endDate: getActivityDisplayDetails(currentActivity?.type!)
          .isSingleDayEvent
          ? undefined
          : createStartOfDayWithTimeZone(end),
      };
      setCurrentActivity(clone);
    }
  };

  const onSlotClick = (start: Date, resource?: IBigCalendarResource) => {
    if (currentActivity && currentActivity.timePeriod) {
      const endDateTime = combineDateAndTime(
        moment(
          currentActivity?.timePeriod?.endDate ??
            currentActivity?.timePeriod?.startDate
        )
          .startOf("day")
          .toDate(),
        currentActivity?.timePeriod?.endTime!
      );
      const startDateTime = combineDateAndTime(
        moment(currentActivity?.timePeriod?.startDate).startOf("day").toDate(),
        currentActivity?.timePeriod?.startTime!
      );
      const minuteGap = getMinuteDifference(startDateTime, endDateTime);

      const newStartDateTime = start;
      const newEndDateTime = moment(newStartDateTime).add(minuteGap, "minutes");

      const clone = { ...currentActivity };
      if (!clone.timePeriod) {
        clone.timePeriod = {};
      }
      clone.timePeriod.startTime = moment(newStartDateTime).format(
        DateFormats.TimeOnlyFormat
      );

      clone.timePeriod.startDate = createStartOfDayWithTimeZone(start);

      clone.timePeriod.endTime = moment(newEndDateTime).format(
        DateFormats.TimeOnlyFormat
      );
      if (
        getActivityDisplayDetails(currentActivity?.type ?? "").isSingleDayEvent
      ) {
        if (
          !isValidTime(clone.timePeriod.startTime, clone.timePeriod.endTime)
        ) {
          clone.timePeriod.endTime = "11:55 PM";
          clone.timePeriod.endDate = clone.timePeriod.startDate;
        }
      }
      clone.timePeriod = {
        startDate: createStartOfDayWithTimeZone(start),
        startTime: moment(newStartDateTime).format(DateFormats.TimeOnlyFormat),
        endTime: clone.timePeriod.endTime,
        endDate: getActivityDisplayDetails(currentActivity?.type ?? "")
          .isSingleDayEvent
          ? undefined
          : createStartOfDayWithTimeZone(newEndDateTime.toDate()),
      };
      assignResourceTypeToActivity(clone, resource);
      setCurrentActivity(clone);
    }
  };

  const invalidCustomerFieldsForTestDrive = customerDetails
    ? getInvalidCustomerFieldsTestDrive(customerDetails, isCustomerAvailable)
    : [];

  return (
    <Fragment>
      <ActivityPopup
        header={
          getActivityDisplayDetails(currentActivity?.type!)?.text ?? "Activity"
        }
        onClose={() => {
          props.onClose();
        }}
      >
        <div className="row mx-0">
          <div className="col-sm-5 col-12 py-4 border-right">
            {(currentActivity?.type === "call" ||
              currentActivity?.type === "deadline" ||
              currentActivity?.type === "meeting" ||
              currentActivity?.type === "task") && (
              <AddActivityForm
                onTimePeriodChange={onTimePeriodChange}
                isEdit={props.isEdit}
                leadId={currentActivity.leadId ?? props.leadId}
                onChangeAssignee={(value) => {
                  setFilter({ salesPerson: value });
                }}
                onDateChange={onDateChange}
                activity={currentActivity}
                onCancel={() => {
                  props.onClose();
                }}
                onSubmit={(activity: ActivityModel) => {
                  onSubmitActivity(activity);
                }}
                onSuccess={props.onClose}
                excludedDates={excludedDatesData}
                onActivityTypeChange={handleOnActivityTypeChange}
              ></AddActivityForm>
            )}
            {currentActivity?.type === "timeOff" && (
              <TimeReservationForm
                onChangeAssignee={(value) => {
                  setFilter({ salesPerson: value });
                }}
                onEditSubmit={(activity: ActivityModel) => {
                  onSubmitActivity(activity);
                }}
                onTimePeriodChange={(data?: ITimePeriodData) => {
                  onTimePeriodChange(data);
                }}
                isEdit={props.isEdit}
                onSuccess={props.onClose}
                onCancel={props.onClose}
                onDateChange={onDateChange}
                leadId={currentActivity.leadId ?? props.leadId}
                activity={timeReservationData}
              ></TimeReservationForm>
            )}
            {currentActivity?.type === "vehicleTimeOff" && (
              <VehicleTimeReservationForm
                onChangeAssignee={(value) => {
                  setFilter({ salesPerson: value });
                }}
                onEditSubmit={(activity: ActivityModel) => {
                  onSubmitActivity(activity);
                }}
                onDataChange={(data) => {
                  setFilter(data);
                }}
                onTimePeriodChange={(data?: ITimePeriodData) => {
                  onTimePeriodChange(data);
                }}
                isEdit={props.isEdit}
                onSuccess={props.onClose}
                onCancel={props.onClose}
                onDateChange={onDateChange}
                leadId={currentActivity.leadId ?? props.leadId}
                activity={timeReservationData}
              ></VehicleTimeReservationForm>
            )}
          </div>
          <div className="col-sm-7 col-12">
            <EditEventCalendarWraper
              eventType={currentActivity?.type}
              isVehicleOnlyView={isVehicleOnlyView()}
              tempEvent={tempEvent}
              formFilter={filter}
              selectedDate={selectedDate!}
              onDateChange={(date: Date) => {
                setSelectedDate(date);
              }}
              onSlotClick={onSlotClick}
              onSelectSlot={onSlotSelection}
              currentActivityType={activityType}
            ></EditEventCalendarWraper>
          </div>
        </div>
      </ActivityPopup>
    </Fragment>
  );
};

export interface IFormFilterData {
  salesPerson?: string;
  showRoom?: string;
  vehicle?: string;
}
